import {
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { setFullWindow } from '../../../../../actions/layout'
import { openMessage } from '../../../../../actions/message'
import { openProfile } from '../../../../../actions/nav'
import { openRoom } from '../../../../../actions/room'
import { toggleFloaterVideo } from '../../../../../app-state/modals/state.floater'
import { FloaterSize, RoomView } from '../../../../../constants/enums'
import { isTouchDevice } from '../../../../../utilities/browser'
import { TransitionTypes } from '../../../../../utilities/transitions'
import { useLayoutSelector } from '../../../../Hooks/useLayout'
import {
    useDialogSelector,
    useDrawerSelector,
} from '../../../../Hooks/useModal'
import { useRoomSelector } from '../../../../Hooks/useRoom'
import { useSwipeAction } from '../../../../Hooks/useSwipeAction'
import { useUserSelector } from '../../../../Hooks/useUser'
import {
    FloaterRegisterOptions,
    checkParent,
    clickParent,
} from '../../../Modals/Floater'
import { minimizeChat, restoreChat } from './common/helpers'
import Banned from './layouts/Banned'
import BasicPM from './layouts/BasicPM'
import FullWindow from './layouts/FullWindow'
import Landscape from './layouts/Landscape'
import Portrait from './layouts/Portrait'

interface PortalProps {
    id: number
    size: FloaterSize
    roomView: RoomView
    ref: React.RefObject<FloaterRegisterOptions>
    isMobile: boolean
    hideVideo: boolean
    loading: boolean
    vertical: boolean
    width: number
    height: number
}

const Portal = (props: PortalProps) => {
    const chatGridContainerRef = useRef<HTMLDivElement>(null)
    const [showZoomVideo, setZoomVideo] = useState(false)
    const [showLoungePreviews, setLoungePreviews] = useState(false)
    const isDialogOpen = useDialogSelector('show')
    const isDrawerOpen = useDrawerSelector('show')
    const disableKeyboardOpenDetection = isDialogOpen || isDrawerOpen

    const { id, size, width, height, vertical, hideVideo, roomView, ref } =
        props
    const isPM = roomView === RoomView.PM
    const isProfile = roomView === RoomView.Profile
    const isFullscreen = size === FloaterSize.Fullscreen

    const isMember = useUserSelector(id, 'isMember')
    const isLounge = useRoomSelector(id, 'isLounge')
    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const banned = useUserSelector(id, 'banned')
    const isKicked = useUserSelector(id, 'isKicked')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isAway = useUserSelector(id, 'isAway')
    const surrogateName = useUserSelector(loungeSurrogateID, 'name')
    const isSurrogateBroadcasting = useUserSelector(
        loungeSurrogateID,
        'isBroadcasting',
    )

    const modelID = !isLounge ? id : loungeSurrogateID // surrogateID

    const fullWindow = useLayoutSelector('fullWindow')

    const showVideo =
        isKicked || banned ? false : isPM || isProfile ? !hideVideo : true
    const noVideoDefault = !isBroadcasting || isAway
    const noProfileVideo =
        isProfile && (noVideoDefault || !showVideo) && isFullscreen

    const [viewingTips, setViewingTips] = useState(false)
    const toggleTips = useCallback(() => setViewingTips((value) => !value), [])

    useEffect(() => {
        // hide video automatically if we're in pm and not broadcasting
        if (isPM && !isBroadcasting && !hideVideo) {
            toggleFloaterVideo(true)
        }
    }, [id, isPM, isBroadcasting, hideVideo]) // ensure this is re-run when id changes

    useEffect(() => {
        if (viewingTips) {
            setViewingTips(false)
        }
    }, [id, fullWindow])
    useEffect(() => {
        if (banned || isKicked) {
            setFullWindow(false)
        }
    }, [banned, isKicked])

    useSwipeAction(chatGridContainerRef, {
        condition: isTouchDevice && fullWindow,
        onSwipeLeft: minimizeChat,
        onSwipeRight: restoreChat,
    })

    // floater registration
    useImperativeHandle(ref, () => {
        return {
            onAction: (event, start, _meta, _data) => {
                if (start) return
                const target = event.target as HTMLElement
                if (size < FloaterSize.Fullscreen) {
                    const floatingRoomVideoContainerContainer =
                        document.querySelector(
                            `#inline-video-container-${modelID}`,
                        ) as HTMLElement
                    const chatHeaderContainer = document.querySelector(
                        `#chat-header-${modelID}`,
                    ) as HTMLElement
                    if (checkParent(target, chatHeaderContainer, 8)) {
                        clickParent(target, 8)
                        return
                    } else if (
                        checkParent(
                            target,
                            floatingRoomVideoContainerContainer,
                            4,
                        )
                    ) {
                        if (isPM) {
                            openMessage(id, TransitionTypes.ROOM)
                        }
                        if (isProfile) {
                            openProfile(id)
                        } else {
                            openRoom(id, {
                                loungeSurrogateID,
                                transitionSource: TransitionTypes.ROOM,
                            })
                        }
                        return
                    }
                    if (target.tagName === 'IMG' && isProfile) {
                        // we've clicked on the profile image to return to fullscreen
                        openRoom(id, {
                            loungeSurrogateID,
                            transitionSource: TransitionTypes.ROOM,
                        })
                    }

                    clickParent(target, 8)
                    return
                }
            },
            onCheck: (_event, _start, _metadata) => {
                if (size < FloaterSize.Fullscreen) return [true, null, false]
                return [true, null, true]
            },
        }
    }, [id, modelID, size, roomView])

    if (!id) return null

    // console.log(
    //     '[FloatingPortal]',
    //     id,
    //     {
    //         banned: roomView !== RoomView.Profile && (banned || isKicked),
    //         basicPM: isPM && !showVideo,
    //         fullWindow,
    //         portrait: vertical || !isFullscreen,
    //     },
    //     {
    //         roomView,
    //         banned,
    //         isKicked,
    //         isPM,
    //         showVideo,
    //         fullWindow,
    //         vertical,
    //         isFullscreen,
    //         hideVideo,
    //         noVideoDefault,
    //         noProfileVideo,
    //     },
    // )

    // always allow folks to see models' profile
    if (!isProfile && (banned || isKicked)) {
        return (
            <Banned
                id={id}
                size={size}
                width={width}
                height={height}
                vertical={vertical}
                fullWindow={fullWindow}
                roomView={roomView}
                disableKeyboardOpenDetection={disableKeyboardOpenDetection}
                showVideo={showVideo}
                setZoomVideo={setZoomVideo}
                viewingTips={viewingTips}
                noProfileVideo={noProfileVideo}
                surrogateName={isLounge ? surrogateName : undefined}
                isSurrogateBroadcasting={
                    isLounge ? isSurrogateBroadcasting : undefined
                }
                toggleTips={toggleTips}
            />
        )
    }

    if (isPM && (!showVideo || isMember)) {
        return (
            <BasicPM
                id={id}
                size={size}
                width={width}
                height={height}
                vertical={vertical}
                fullWindow={fullWindow}
                roomView={roomView}
                disableKeyboardOpenDetection={disableKeyboardOpenDetection}
                showVideo={showVideo}
                setZoomVideo={setZoomVideo}
                viewingTips={viewingTips}
                noProfileVideo={noProfileVideo}
                surrogateName={isLounge ? surrogateName : undefined}
                isSurrogateBroadcasting={
                    isLounge ? isSurrogateBroadcasting : undefined
                }
                toggleTips={toggleTips}
            />
        )
    }

    if (fullWindow) {
        return (
            <FullWindow
                id={id}
                size={size}
                width={width}
                height={height}
                vertical={vertical}
                fullWindow={fullWindow}
                roomView={roomView}
                disableKeyboardOpenDetection={disableKeyboardOpenDetection}
                showVideo={showVideo}
                viewingTips={viewingTips}
                noProfileVideo={noProfileVideo}
                surrogateName={isLounge ? surrogateName : undefined}
                isSurrogateBroadcasting={
                    isLounge ? isSurrogateBroadcasting : undefined
                }
                toggleTips={toggleTips}
            />
        )
    }

    if (vertical || !isFullscreen) {
        return (
            <Portrait
                id={id}
                size={size}
                width={width}
                height={height}
                vertical={vertical}
                fullWindow={fullWindow}
                roomView={roomView}
                disableKeyboardOpenDetection={disableKeyboardOpenDetection}
                showVideo={showVideo}
                showZoomVideo={showZoomVideo}
                setZoomVideo={setZoomVideo}
                showLoungePreviews={showLoungePreviews}
                setLoungePreviews={setLoungePreviews}
                viewingTips={viewingTips}
                noProfileVideo={noProfileVideo}
                noVideoDefault={noVideoDefault}
                surrogateName={isLounge ? surrogateName : undefined}
                isSurrogateBroadcasting={
                    isLounge ? isSurrogateBroadcasting : undefined
                }
                toggleTips={toggleTips}
            />
        )
    }

    return (
        <Landscape
            id={id}
            size={size}
            width={width}
            height={height}
            vertical={vertical}
            fullWindow={fullWindow}
            roomView={roomView}
            disableKeyboardOpenDetection={disableKeyboardOpenDetection}
            showVideo={showVideo}
            showZoomVideo={showZoomVideo}
            showLoungePreviews={showLoungePreviews}
            setLoungePreviews={setLoungePreviews}
            viewingTips={viewingTips}
            noProfileVideo={noProfileVideo}
            surrogateName={isLounge ? surrogateName : undefined}
            isSurrogateBroadcasting={
                isLounge ? isSurrogateBroadcasting : undefined
            }
            toggleTips={toggleTips}
        />
    )
}

export default memo(Portal)
